// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import type { FeatureFlags } from "@/lib/feature-flags";
import { FLAG_KEYS, useFeatureFlags } from "@/lib/feature-flags";

interface FeatureFlagsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FeatureFlagsDialog({ open, onOpenChange }: FeatureFlagsDialogProps) {
  const { t } = useTranslation("back-office");
  const flags = useFeatureFlags();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{t("commandPalette.featureFlags")}</DialogTitle>
        </DialogHeader>
        <div className="divide-y">
          {(Object.entries(FLAG_KEYS) as [keyof FeatureFlags, string][]).map(([local, remote]) => (
            <div key={local} className="flex items-center justify-between py-2.5">
              <div>
                <p className="text-sm font-medium">{local}</p>
                <p className="text-xs text-muted-foreground">{remote}</p>
              </div>
              <Badge variant={flags[local] ? "default" : "secondary"}>
                {flags[local] ? t("commandPalette.flagOn") : t("commandPalette.flagOff")}
              </Badge>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
